import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';

const sessionEsignSchema = z.object({
  documentId: z.string(),
  /** Where the applicant signs. Null when every signer is emailed instead. */
  applicantSignLink: z.string().nullable(),
  /** Addresses SignCare emails the signing link to. */
  emailedSigners: z.array(z.string()).default([]),
  /** The applicant's signer id — what the signing frame watches for completion. */
  applicantSignerId: z.string().nullable().default(null),
});

export type SessionEsign = z.infer<typeof sessionEsignSchema>;

/**
 * Generates the document and sends it for signature.
 *
 * BILLED — one signature request per signer — so this is never called on render.
 * The backend is idempotent on the step's stored result, so a retry after a dropped
 * connection returns the links already issued instead of buying a second document.
 */
export function useCreateEsign(sessionId: string | undefined, stepId: string) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();

  return useMutation({
    mutationFn: async () => {
      const path = publicRun
        ? `api/v1/public/sessions/${publicRun.token}/steps/${stepId}/esign`
        : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/esign`;
      const client = publicRun ? publicApiClient : apiClient;

      // Generating the document and raising the request is slower than a plain read.
      const raw = await client.post(path, { timeout: 90_000 }).json();
      return sessionEsignSchema.parse(raw);
    },
  });
}
